import React, { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlass,
  PaperPlaneRight,
  User,
  Clock,
  Check,
  Checks,
  Phone,
  WhatsappLogo,
  ChatCircleDots,
  ArrowsClockwise,
  PencilSimple,
  X,
  CaretLeft
} from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { chatService } from '../../services';
import './Chat.css';

// ─── Shared chat UI (no Header) — used in WhatsApp page too ──
export function ChatContent() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [composeLoading, setComposeLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { scrollToBottom(); }, [messages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await chatService.getConversations();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChatHistory = async (phone) => {
    try {
      setLoading(true);
      const data = await chatService.getChatHistory(phone);
      setMessages(data.messages || []);
      setSelectedConversation({ phone, customer: data.customer });
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedConversation) return;
    try {
      setSending(true);
      const result = await chatService.sendMessage(
        selectedConversation.phone,
        messageInput.trim()
      );
      const newMessage = {
        id: result.messageId || Date.now(),
        direction: 'outbound',
        source: 'admin-chat',
        type: 'text',
        text: messageInput.trim(),
        senderName: 'Admin',
        isBot: false,
        sentByAdmin: true,
        timestamp: new Date().toISOString(),
        status: 'sent'
      };
      setMessages(prev => [...prev, newMessage]);
      setMessageInput('');
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setSearchResults([]); return; }
    try {
      const data = await chatService.searchCustomers(query);
      setSearchResults(data.customers || []);
    } catch (error) {
      console.error('Failed to search customers:', error);
    }
  };

  const handleSelectSearchResult = (customer) => {
    loadChatHistory('91' + customer.phone);
    setSearchQuery('');
    setSearchResults([]);
  };

  const openCompose = async () => {
    setShowCompose(true);
    setComposeSearch('');
    try {
      setComposeLoading(true);
      const data = await chatService.getAllCustomers('', 1, 100);
      setAllCustomers(data.customers || data.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setComposeLoading(false);
    }
  };

  const filteredCustomers = allCustomers.filter(c => {
    const q = composeSearch.toLowerCase();
    return c.customerName?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  const handleComposeSelect = (customer) => {
    loadChatHistory('91' + customer.phone);
    setShowCompose(false);
    setComposeSearch('');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="chat-container">
      {/* Conversations Sidebar */}
      <div className={`conversations-sidebar ${selectedConversation ? 'has-selection' : ''}`}>
        <div className="sidebar-header">
          <h3><ChatCircleDots size={20} /> Conversations</h3>
          <div className="sidebar-actions">
            <button className="compose-btn" onClick={openCompose} title="New Chat">
              <PencilSimple size={18} />
            </button>
            <button className="refresh-btn" onClick={loadConversations} disabled={loading}>
              <ArrowsClockwise size={18} />
            </button>
          </div>
        </div>

        {/* New Chat Compose Panel */}
        {showCompose && (
          <div className="compose-panel">
            <div className="compose-header">
              <span>New Chat</span>
              <button className="compose-close-btn" onClick={() => setShowCompose(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="compose-search-wrapper">
              <MagnifyingGlass size={16} />
              <input
                type="text"
                placeholder="Search name or number..."
                value={composeSearch}
                onChange={e => setComposeSearch(e.target.value)}
                className="compose-search-input"
                autoFocus
              />
            </div>
            <div className="compose-list">
              {composeLoading ? (
                <div className="compose-loading">Loading...</div>
              ) : filteredCustomers.length === 0 ? (
                <div className="compose-empty">No customers found</div>
              ) : (
                filteredCustomers.map(c => (
                  <div key={c._id} className="compose-item" onClick={() => handleComposeSelect(c)}>
                    <div className="compose-avatar"><User size={20} /></div>
                    <div className="compose-info">
                      <span className="compose-name">{c.customerName}</span>
                      <span className="compose-phone"><Phone size={12} /> +91 {c.phone}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <MagnifyingGlass size={18} />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((customer) => (
                <div key={customer._id} className="search-result-item"
                  onClick={() => handleSelectSearchResult(customer)}>
                  <User size={20} />
                  <div className="result-info">
                    <span className="result-name">{customer.customerName}</span>
                    <span className="result-phone">{customer.phone}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conversations List */}
        <div className="conversations-list">
          {loading && conversations.length === 0 ? (
            <div className="loading-state">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="empty-state">
              <ChatCircleDots size={48} weight="thin" />
              <p>No conversations yet</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.phone}
                className={`conversation-item ${selectedConversation?.phone === conv.phone ? 'active' : ''}`}
                onClick={() => loadChatHistory(conv.phone)}
              >
                <div className="conversation-avatar"><User size={24} /></div>
                <div className="conversation-info">
                  <div className="conversation-header">
                    <span className="conversation-name">{conv.customerName}</span>
                    <span className="conversation-time">{formatDate(conv.lastMessage)}</span>
                  </div>
                  <div className="conversation-preview">
                    <Phone size={14} />
                    <span>{conv.phone.replace(/^91/, '+91 ')}</span>
                  </div>
                  {conv.lastMessageText && (
                    <div className="conversation-last-message">{conv.lastMessageText}</div>
                  )}
                </div>
                {conv.messageCount > 0 && (
                  <div className="message-count">{conv.messageCount}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`chat-area ${selectedConversation ? 'has-selection' : ''}`}>
        {selectedConversation ? (
          <>
            <div className="chat-header">
              <button
                className="chat-back-btn"
                onClick={() => setSelectedConversation(null)}
                aria-label="Back to conversations"
              >
                <CaretLeft size={20} />
              </button>
              <div className="chat-customer-info">
                <div className="customer-avatar"><User size={28} weight="fill" /></div>
                <div className="customer-details">
                  <h3>{selectedConversation.customer?.name || 'Unknown'}</h3>
                  <span className="customer-phone">
                    <Phone size={14} />
                    {selectedConversation.phone.replace(/^91/, '+91 ')}
                  </span>
                </div>
              </div>
            </div>

            <div className="messages-container">
              {loading && messages.length === 0 ? (
                <div className="loading-messages">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <WhatsappLogo size={48} weight="thin" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (() => {
                const firstAdminIdx = messages.findIndex(m => m.sentByAdmin);
                return messages.map((msg, idx) => {
                  const isSent = msg.direction === 'outbound';
                  const isBot  = !!msg.isBot;
                  const senderName = msg.senderName ||
                    (isSent ? 'Admin' : selectedConversation.customer?.name || 'Customer');
                  let bubbleClass = 'received';
                  if (isSent) bubbleClass = isBot ? 'sent bot' : 'sent admin';

                  return (
                    <React.Fragment key={msg.id}>
                      {firstAdminIdx >= 0 && idx === firstAdminIdx && (
                        <div className="chat-handover-divider">
                          <span className="chat-handover-line" />
                          <span className="chat-handover-text">Bot conversation ended &middot; Admin took over</span>
                          <span className="chat-handover-line" />
                        </div>
                      )}
                      <div className={`message ${bubbleClass}`}>
                        <div className="message-bubble">
                          <span className="message-sender-name">{senderName}</span>
                          {msg.type === 'image' && <span className="message-media-tag">📷 Photo{msg.text ? ` — ${msg.text}` : ''}</span>}
                          {msg.type === 'video' && <span className="message-media-tag">🎥 Video{msg.text ? ` — ${msg.text}` : ''}</span>}
                          {msg.type === 'audio' && <span className="message-media-tag">🎙️ Voice message</span>}
                          {msg.type === 'document' && <span className="message-media-tag">📄 Document{msg.text ? `: ${msg.text}` : ''}</span>}
                          {msg.type === 'location' && <span className="message-media-tag">📍 Location{msg.text ? `: ${msg.text}` : ''}</span>}
                          {(msg.type === 'interactive' || msg.type === 'button') && <span className="message-media-tag">⚡ {msg.text || 'Button reply'}</span>}
                          {(msg.type === 'text' || !msg.type) && (
                            <p className="message-text">{msg.text || <em className="message-empty">Empty message</em>}</p>
                          )}
                          <div className="message-meta">
                            <span>{formatTime(msg.timestamp)}</span>
                            {isSent && (() => {
                              const s = msg.status;
                              if (s === 'read')      return <Checks size={15} className="msg-tick tick-read" />;
                              if (s === 'delivered') return <Checks size={15} className="msg-tick tick-delivered" />;
                              if (s === 'sent')      return <Check  size={15} className="msg-tick tick-sent" />;
                              return <Clock size={12} className="msg-tick tick-pending" />;
                            })()}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
              <div ref={messagesEndRef} />
            </div>

            <form className="message-input-container" onSubmit={handleSendMessage}>
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="message-input"
                disabled={sending}
              />
              <button
                type="submit"
                className="send-button"
                disabled={!messageInput.trim() || sending}
                title={sending ? 'Sending…' : 'Send'}
              >
                <PaperPlaneRight size={22} weight="fill" />
              </button>
            </form>
          </>
        ) : (
          <div className="no-conversation-selected">
            <WhatsappLogo size={64} weight="thin" />
            <h3>WhatsApp Business Chat</h3>
            <p>Select a conversation or start a new one</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Full-page Chat (standalone route — kept for compatibility) ─
const Chat = () => (
  <div className="chat-page">
    <Header title="WhatsApp Chat" icon={<WhatsappLogo size={24} weight="fill" />} />
    <ChatContent />
  </div>
);

export default Chat;
